import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Editor } from "@monaco-editor/react";
import { submitTask, getTaskBySlug } from "../../features/courses/courseSlice";
import TaskResultPanel from "./TaskResultPanel";
import { FiCheckCircle } from "react-icons/fi";
import TaskDebugOutput from "./TaskDebugOutput";

function TaskEmbed({ taskSlug, taskNumber, isActive, onToggle, onProgressUpdate }) {
  const dispatch = useDispatch();
  const { taskData, taskExecutionStatus } = useSelector((state) => state.courses);

  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [task, setTask] = useState(null);
  const [progress, setProgress] = useState(null);
  const [showDebugLogs, setShowDebugLogs] = useState(false);
  const editorContainerRef = useRef(null);

  useEffect(() => {
    // Fetch task data by slug

    const fetchTask = async () => {
      try {
        const result = await dispatch(getTaskBySlug(taskSlug)).unwrap();
        setTask(result.data.task);
        setProgress(result.data.progress);
        setCode(result.data.progress?.lastSubmission || result.data.task.boilerPlateCode || "");

        // Notify parent of progress update
        if (onProgressUpdate && result.data.progress) {
          onProgressUpdate(taskSlug, result.data.progress);
        }
      } catch (err) {
        console.error("Error fetching task:", err);
      }
    };

    fetchTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskSlug, dispatch]);

  // Handle scroll containment for editor
  useEffect(() => {
    const editorContainer = editorContainerRef.current;
    if (!editorContainer) return;

    const handleWheel = (e) => {
      // Find the Monaco editor scrollable element
      const scrollableElement = editorContainer.querySelector('.monaco-scrollable-element');
      if (!scrollableElement) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollableElement;
      const isScrollingDown = e.deltaY > 0;
      const isScrollingUp = e.deltaY < 0;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;

      // Prevent page scroll if we're not at the boundaries
      if ((isScrollingDown && !isAtBottom) || (isScrollingUp && !isAtTop)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    editorContainer.addEventListener('wheel', handleWheel, { passive: false });
    return () => editorContainer.removeEventListener('wheel', handleWheel);
  }, [task]);

  // Auto-expand debug logs when task is run
  useEffect(() => {
    const hasResults = taskExecutionStatus?.[task?._id];
    if (hasResults) {
      console.log('[TaskEmbed] Task execution results:', {
        taskId: task?._id,
        hasDebugLogs: !!hasResults.debugLogs,
        debugLogsLength: hasResults.debugLogs?.length || 0,
        debugLogsType: typeof hasResults.debugLogs,
        debugLogsPreview: hasResults.debugLogs?.substring(0, 100),
      });
    }
    if (hasResults?.debugLogs) {
      setShowDebugLogs(true);
    }
  }, [taskExecutionStatus, task?._id]);


  const handleRun = async () => {
    if (!task) return;

    setIsSubmitting(true);
    try {
      const result = await dispatch(
        submitTask({
          taskId: task._id,
          apexBody: code,
          lessonType: task.type || "apex",
          runTestOnly: false,
        })
      ).unwrap();

      // Update progress after submission
      if (result.submissionResults) {
        setProgress(result.submissionResults);

        // Notify parent of progress update
        if (onProgressUpdate) {
          onProgressUpdate(taskSlug, result.submissionResults);
        }
      }
    } catch (err) {
      if (err.response && err.response.data) {
        console.error("Error submitting task:", err.response.data);
      } else {
        console.error("Error submitting task:", err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!task) {
    return (
      <div
        className="rounded-lg p-4"
        style={{
          background: 'rgba(39, 39, 42, 0.5)',
          border: '1px solid rgba(113, 113, 122, 0.3)',
        }}
      >
        <p className="text-sm text-gray-400">Loading task...</p>
      </div>
    );
  }

  const isCompleted = progress?.status === "Completed";
  const hasResults = taskExecutionStatus?.[task._id];

  // Calculate pass/fail summary
  const getTestSummary = () => {
    if (!hasResults?.data?.exceptionMessage) return null;

    try {
      const results = JSON.parse(
        hasResults.data.exceptionMessage.replace("TestResultException: ", "")
      );
      if (Array.isArray(results)) {
        const passed = results.filter(r => r.pass).length;
        const total = results.length;
        return { passed, total, allPassed: passed === total };
      }
    } catch (e) {
      return null;
    }
    return null;
  };

  const testSummary = getTestSummary();

  return (
    <div
      className="task-card rounded-xl overflow-hidden transition-all"
      style={{
        background: isActive
          ? 'rgba(39, 39, 42, 0.8)'
          : 'rgba(39, 39, 42, 0.4)',
        border: isActive
          ? '2px solid rgba(165, 138, 194, 0.5)'
          : '1px solid rgba(113, 113, 122, 0.3)',
        boxShadow: isActive
          ? '0 8px 24px rgba(165, 138, 194, 0.2)'
          : '0 2px 8px rgba(0, 0, 0, 0.3)',
      }}
    >
      {/* Task Header - Collapsible */}
      <div
        className="px-4 py-3 cursor-pointer transition-all"
        style={{
          background: isActive
            ? 'linear-gradient(135deg, rgba(165, 138, 194, 0.15), rgba(145, 118, 174, 0.1))'
            : 'rgba(24, 24, 27, 0.5)',
        }}
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-xs font-bold text-zinc-400">Task {taskNumber}</span>
            {isCompleted && (
              <FiCheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#86b27a' }} title="Completed" />
            )}
            <h3 className="text-sm font-semibold text-white truncate">
              {task.name}
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {testSummary && (
              <span
                className="text-xs px-2 py-1 rounded font-medium"
                style={{
                  background: testSummary.allPassed
                    ? 'rgba(134, 178, 122, 0.2)'
                    : 'rgba(239, 68, 68, 0.2)',
                  color: testSummary.allPassed
                    ? 'rgba(184, 212, 168, 1)'
                    : 'rgba(252, 165, 165, 1)',
                  border: `1px solid ${testSummary.allPassed
                    ? 'rgba(134, 178, 122, 0.3)'
                    : 'rgba(239, 68, 68, 0.3)'}`,
                }}
              >
                {testSummary.passed}/{testSummary.total}
              </span>
            )}
            <span className="text-zinc-500 text-sm">
              {isActive ? "▼" : "▶"}
            </span>
          </div>
        </div>
        {/* Progress Info Bar */}
        {progress && !isActive && (
          <div className="mt-2 flex items-center gap-3 text-xs text-zinc-400">
            <span className={isCompleted ? "" : "text-zinc-400"} style={isCompleted ? { color: '#86b27a' } : {}}>
              {progress.status || "Not Started"}
            </span>
            {progress.attemptCounter > 0 && (
              <>
                <span>•</span>
                <span>{progress.attemptCounter} attempt{progress.attemptCounter !== 1 ? 's' : ''}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Expanded Content */}
      {isActive && (
        <div className="border-t" style={{ borderColor: 'rgba(113, 113, 122, 0.3)' }}>
          {/* Code Editor */}
          <div
            ref={editorContainerRef}
            className="relative"
            style={{
              background: 'rgba(24, 24, 27, 0.6)',
            }}
          >
            <Editor
              height="300px"
              language="apex"
              theme="vs-dark"
              value={code}
              onChange={(value) => setCode(value || "")}
              options={{
                fontSize: 13,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                lineNumbers: "on",
                roundedSelection: false,
                scrollbar: {
                  vertical: "visible",
                  horizontal: "visible",
                  useShadows: false,
                  verticalScrollbarSize: 8,
                  horizontalScrollbarSize: 8,
                },
              }}
            />
          </div>

          {/* Action Buttons */}
          <div
            className="px-4 py-3 flex gap-2 border-t"
            style={{
              background: 'rgba(24, 24, 27, 0.5)',
              borderColor: 'rgba(113, 113, 122, 0.25)',
            }}
          >
            <button
              onClick={handleRun}
              disabled={isSubmitting}
              className="px-4 py-2 rounded-lg font-medium text-sm text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-1"
              style={{
                background: isSubmitting
                  ? 'rgba(165, 138, 194, 0.4)'
                  : 'linear-gradient(135deg, rgba(165, 138, 194, 0.7), rgba(145, 118, 174, 0.7))',
                border: '1px solid rgba(165, 138, 194, 0.4)',
              }}
            >
              {isSubmitting ? "Running..." : "▶ Run"}
            </button>
            <button
              onClick={() => setCode(task.boilerPlateCode || "")}
              className="px-4 py-2 rounded-lg font-medium text-sm text-zinc-300 transition-all hover:text-white"
              style={{
                background: 'rgba(63, 63, 70, 0.5)',
                border: '1px solid rgba(113, 113, 122, 0.4)',
              }}
            >
              Reset
            </button>
          </div>

          {/* Results Section */}
          {hasResults ? (
            <div className="px-4 py-4 space-y-4">
              {/* Always show compilation errors, even if hideExecutionResults is true */}
              {hasResults.data?.compileProblem ? (
                <div>
                  <TaskResultPanel results={hasResults} />
                </div>
              ) : !task.hideExecutionResults ? (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                      Execution Results
                    </h4>
                    {testSummary && (
                      <span
                        className="text-xs px-2 py-1 rounded font-medium"
                        style={{
                          background: testSummary.allPassed
                            ? 'rgba(134, 178, 122, 0.15)'
                            : 'rgba(239, 68, 68, 0.15)',
                          color: testSummary.allPassed
                            ? 'rgba(184, 212, 168, 1)'
                            : 'rgba(252, 165, 165, 1)',
                        }}
                      >
                        {testSummary.allPassed ? "✓ All Passed" : `${testSummary.passed}/${testSummary.total} Passed`}
                      </span>
                    )}
                  </div>
                  <TaskResultPanel results={hasResults} />
                </div>
              ) : null}

              {/* Debug Logs - Collapsible */}
              {hasResults.debugLogs && (
                <div>
                  <button
                    onClick={() => setShowDebugLogs(!showDebugLogs)}
                    className="w-full flex items-center justify-between mb-2 text-xs font-semibold text-zinc-400 uppercase tracking-wider hover:text-zinc-300 transition-colors"
                  >
                    <span>Debug Logs</span>
                    <span className="text-zinc-500 text-sm">
                      {showDebugLogs ? "▼" : "▶"}
                    </span>
                  </button>
                  {showDebugLogs && <TaskDebugOutput logs={hasResults.debugLogs} />}
                </div>
              )}
            </div>
          ) : (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-zinc-400">Click Run to test your code</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TaskEmbed;
